import React, { useContext, useMemo } from 'react'

import { Typography } from '@mui/material'
import { Box, ButtonV2, Icon, Link, Row } from '@vividseats/vivid-ui-kit'
import classNames from 'classnames'
import NextLink from 'next/link'
import { useRouter } from 'next/router'

import { getDisplayText, getRefererIsExternal } from '@/components/pages/production/utils'
import productionsDetailsContext from '@/context/production-details'
import regionContext from '@/context/region'
import { useDesktopMediaQuery } from '@/utils'
import { getProductionPageDateTime } from '@/utils/dates'
import { getPerformerUrl, getVenueLocation } from '@/utils/production'

import DesktopPerformerImage from '../desktop-performer-image'
import MoreInfoButton from '../more-info-button'

import styles from './styles.module.scss'

interface ProductionDetailBarProps {
    referer?: string
    isPlainPageSource?: boolean
}

const ProductionDetailBar: React.FC<ProductionDetailBarProps> = ({ referer, isPlainPageSource }) => {
    const isDesktop = useDesktopMediaQuery()
    const router = useRouter()

    const {
        state: { data },
        selectors: { heroData, productionVenueInfo, eventInfo },
    } = useContext(productionsDetailsContext)

    const {
        selectors: { region },
    } = useContext(regionContext)

    const formattedTitle = useMemo(() => {
        const productionName = data?.name

        if (!productionName) return null

        return (
            <div className={classNames(styles.title, styles.truncate)}>
                {heroData?.href ? (
                    <Link
                        importance="none"
                        href={heroData.href}
                        className={styles.link}
                        data-testid="production-detail-bar-performer-link"
                    >
                        {productionName}
                    </Link>
                ) : (
                    productionName
                )}
            </div>
        )
    }, [data?.name, heroData?.href])

    if (!data) return null

    const refererIsExternal = getRefererIsExternal(referer)

    const {
        formattedDate: { date, time },
        venue: { name: venueName, webPath: venueURL },
        performers,
    } = data

    // TODO: BWCT-326
    const dateTimeContent = getProductionPageDateTime({ date, time })

    const performerUrl = getPerformerUrl(performers[0])

    const locationName = getVenueLocation(productionVenueInfo) || eventInfo?.locationName
    const { locationDisplayName, venueDisplayName, separator } = getDisplayText(locationName, venueName)

    if (isPlainPageSource) {
        return (
            <h1 data-testid="production-details-source-text" className={styles.productionDetailsSource}>
                {data?.name} {venueDisplayName} {separator} {locationDisplayName} {dateTimeContent}
            </h1>
        )
    }

    return (
        <Row justify="left" align="center" className={styles.row}>
            <Box mobileDisplay="flex" tabletDisplay="flex" desktopDisplay="none">
                {refererIsExternal ? (
                    <Link
                        href={performerUrl}
                        LinkComponent={NextLink}
                        className={styles.backButton}
                        data-testid="main-performer-back-button"
                    >
                        <Icon type="carat-left" size="md" />
                    </Link>
                ) : (
                    <ButtonV2
                        className={styles.backButton}
                        importance="text"
                        onClick={() => router.back()}
                        data-testid="back-button"
                    >
                        <Icon type="carat-left" size="md" />
                    </ButtonV2>
                )}
            </Box>
            <Box mobileDisplay="none" tabletDisplay="none" desktopDisplay="flex">
                <DesktopPerformerImage alt={eventInfo?.mainPerformerName} />
            </Box>

            <Box flexGrow className={styles.titleWrapper}>
                <h1 data-testid="production-detail-bar-text">
                    <Box mobileDisplay="none" tabletDisplay="none" desktopDisplay="flex">
                        <Typography variant="body-bold">{formattedTitle}</Typography>
                    </Box>
                    <Box mobileDisplay="flex" tabletDisplay="flex" desktopDisplay="none">
                        <Typography variant="small-bold">{formattedTitle}</Typography>
                    </Box>

                    {' ' /* space needed for SEO */}

                    <Typography variant={isDesktop ? 'small-regular' : 'caption-regular'} color="gray.600">
                        <div className={classNames(styles.label, styles.truncate)}>
                            {venueURL ? (
                                <Link
                                    importance="none"
                                    LinkComponent={NextLink}
                                    href={venueURL}
                                    className={styles.link}
                                    data-testid="production-detail-bar-venue-link"
                                >
                                    {venueDisplayName}
                                </Link>
                            ) : (
                                venueDisplayName
                            )}
                            {/* We need to use an actual space instead of &nbsp; so that truncation is not affected */}{' '}
                            {separator}{' '}
                            {region?.webPath ? (
                                <Link
                                    importance="none"
                                    LinkComponent={NextLink}
                                    href={region.webPath}
                                    className={styles.link}
                                    data-testid="production-detail-bar-location-link"
                                >
                                    {locationDisplayName}
                                </Link>
                            ) : (
                                locationDisplayName
                            )}
                        </div>

                        {' ' /* space needed for SEO */}
                        <div className={styles.alignItemsCenter}>
                            {dateTimeContent}
                            <MoreInfoButton className={styles.moreInfoButton} fontSize="0.75rem" />
                        </div>
                    </Typography>
                </h1>
            </Box>
        </Row>
    )
}

export default ProductionDetailBar
